import {
  useCallback, useEffect, useMemo, useState,
} from 'react';

import { useCroptimizer } from 'features/Croptimizer/providers/CroptimizerContext';
import useCroptimizerCrop from 'api/hooks/useCroptimizerCrop';
import useMaskedInput from 'hooks/useMaskedInput';
import { Metric } from 'utils/constants';
import {
  mtHaToTonAc, unMaskInputValue, mtTot,
  unMaskInput,
} from 'utils/helpers';
import { BoardCrop } from 'features/Croptimizer/types';
import { TypeOfKey } from 'utils/types';
import { yieldPercentHelper } from 'features/Croptimizer/utils/calcHelperBoard';

let preferredDefaultCropIndex: number | undefined;
let wasInitialized = false;

const DEFAULT_CROP_NAME = 'Blé tendre';

export const useCropBoard = (options?: { defaultCropIndex?: number }) => {
  const defaultCropIndex = options?.defaultCropIndex ?? 0;

  if (typeof options?.defaultCropIndex === 'number') {
    preferredDefaultCropIndex = options.defaultCropIndex;
  }

  const { data: crop } = useCroptimizerCrop();

  const {
    boardCropValues,
    setBoardCropValues,
    metricValue,
    boardStatusValues,
  } = useCroptimizer();
  const [initialCurrentYield, setInitialCurrentYield] = useState<string>('');
  const [initialPrice, setInitialPrice] = useState<string>('');

  const cropSelect = useMemo(() => {
    if (crop) {
      return crop?.map((crop) => ({
        value: crop.Crops,
        label: crop.Crops,
      }));
    }

    return [];
  }, [crop]);

  const getDefaultCropValue = useCallback(() => {
    const preferredByName = cropSelect.find(
      (option) => option.value === DEFAULT_CROP_NAME,
    )?.value;

    if (preferredByName) {
      return preferredByName;
    }

    const index = preferredDefaultCropIndex ?? defaultCropIndex;

    return cropSelect[index]?.value ?? cropSelect[0]?.value ?? '';
  }, [cropSelect, defaultCropIndex]);

  const changeBoardCropValues = useCallback(
    (name: keyof BoardCrop) => (value: TypeOfKey<BoardCrop>) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      setBoardCropValues((prevValues: BoardCrop | null) => ({
        ...(prevValues ?? {}),
        [name]: value,
      }));
    },
    [],
  );

  const {
    value: currentYield,
    setValue: setCurrentYield,
    clean: cleancurrentyield,
  } = useMaskedInput({
    defaultValue: '',
    value: boardCropValues?.currentYield ?? '',
    onChangeCallback: changeBoardCropValues('currentYield'),
  });

  const {
    value: price,
    setValue: setPrice,
    clean: cleanprice,
  } = useMaskedInput({
    defaultValue: '',
    value: boardCropValues?.price ?? '',
    onChangeCallback: changeBoardCropValues('price'),
  });

  const { value: cropGroupYield, setValue: setCropGroupYield } = useMaskedInput(
    {
      defaultValue: '0',
      value: boardCropValues?.cropGroupYield ?? '0',
      onChangeCallback: changeBoardCropValues('cropGroupYield'),
      isDisableClean: true,
    },
  );

  const { value: cropType, setValue: setCropType } = useMaskedInput({
    defaultValue: getDefaultCropValue(),
    value: boardCropValues?.crop ?? '',
    onChangeCallback: (value) => {
      const isActuallyChanging = boardCropValues?.crop && boardCropValues.crop !== value;
      const isInitializing = !boardCropValues?.crop;
      
      changeBoardCropValues('crop')(value);

      const getCrop = crop?.find((crops) => crops.Crops === value);
      const resultYield =
        metricValue === Metric.metric
          ? String(getCrop?.PotentialYeild)
          : String(mtHaToTonAc(Number(getCrop?.PotentialYeild)));

      if (isInitializing || isActuallyChanging) {
        setCurrentYield(unMaskInputValue(resultYield));
        setInitialCurrentYield(unMaskInputValue(resultYield));
      }
      
      const resultPrice =
        metricValue === Metric.metric
          ? String(getCrop?.Price)
          : String(mtTot(Number(getCrop?.Price)));

      if (isInitializing || isActuallyChanging) {
        setPrice(unMaskInputValue(resultPrice));
        setInitialPrice(unMaskInputValue(resultPrice));
      }
      
      setCropGroupYield(getCrop?.GroupYeildLosses ?? 0);
    },
    isDisableClean: true,
  });

  useEffect(() => {
    if (!cropSelect?.length) {
      return;
    }

    if (boardCropValues?.crop && boardCropValues?.price) {
      return;
    }

    if (boardCropValues?.crop) {
      return;
    }

    const preferredIndex = preferredDefaultCropIndex;
    const preferredByName = cropSelect.find(
      (option) => option.value === DEFAULT_CROP_NAME,
    )?.value;
    const preferredValue =
      preferredByName
      ?? (typeof preferredIndex === 'number' ? cropSelect[preferredIndex]?.value : undefined);

    const shouldInit =
      !boardCropValues?.crop
      || (
        !!preferredValue
        && boardCropValues?.crop === cropSelect[0]?.value
        && preferredValue !== cropSelect[0]?.value
        && !wasInitialized
      );

    if (!shouldInit) {
      return;
    }

    const defaultCropValue = preferredValue ?? getDefaultCropValue();

    if (defaultCropValue) {
      setCropType(defaultCropValue);
    }
  }, [cropSelect.length, boardCropValues?.crop, boardCropValues?.price, getDefaultCropValue]); // Changed dependency to cropSelect.length instead of cropSelect

  useEffect(() => () => {
    if (typeof options?.defaultCropIndex === 'number'
      && preferredDefaultCropIndex === options.defaultCropIndex) {
      preferredDefaultCropIndex = undefined;
    }
  }, []);

  const getYieldGain = useMemo(() => {
    if (cropType?.length > 0 && crop && boardStatusValues) {
      const getCrop = crop?.find((crops) => crops.Crops === cropType);
      const yeildLossesCurrentTargetPH = yieldPercentHelper(
        getCrop?.GroupYeildLosses || 0,
        +unMaskInput(boardStatusValues.currentPh),
        +unMaskInput(boardStatusValues.targetPh),
      );

      return yeildLossesCurrentTargetPH;
    }

    return '';
  }, [cropType, crop, boardStatusValues]);

  const getYieldGainMtha = useMemo(() => {
    const result =
      getYieldGain && getYieldGain?.length > 0 && cleancurrentyield
        ? String(
          (Number(cleancurrentyield) * (Number(getYieldGain) / 100)).toFixed(
            3,
          ),
        )
        : '0';

    return result;
  }, [getYieldGain, cleancurrentyield]);

  const getYieldGainPrice = useMemo(() => {
    const valueResult = Number(getYieldGainMtha) * Number(cleanprice);

    return getYieldGainMtha && cleanprice ? String(valueResult.toFixed(2)) : '';
  }, [getYieldGainMtha, price]);

  const handleCropChange = (e: any) => {
    wasInitialized = true;
    setCropType(e.value);
  };

  const resetData = () => {
    const defaultCropValue = getDefaultCropValue();

    if (defaultCropValue) {
      wasInitialized = false;
      setCropType(defaultCropValue);
    }
  };

  return {
    changeBoardCropValues,
    handleCropChange,
    setCurrentYield,
    setPrice,
    resetData,
    currentYield,
    cleancurrentyield,
    price,
    cleanprice,
    cropGroupYield,
    cropType,
    cropSelect,
    initialCurrentYield,
    initialPrice,
    getYieldGain,
    getYieldGainPrice,
    getYieldGainMtha,
  };
}; 